import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Shield,
  Users,
  Building2,
  Activity,
  TrendingUp,
  Calendar,
  CheckSquare,
  AlertCircle,
  ArrowUpRight,
  UserPlus,
  Settings,
  ListTodo,
  BrainCircuit,
  XCircle,
  CheckCircle2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { api } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const StatCard = ({ icon: Icon, label, value, subValue, color = 'indigo', onClick }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    whileHover={{ scale: 1.02 }}
    className="cursor-pointer"
    onClick={onClick}
  >
    <Card className="gds-card h-full">
      <CardContent className="p-6">
        <div className="flex items-center gap-4">
          <div className={`w-12 h-12 rounded-xl bg-${color}-100 dark:bg-${color}-900/30 flex items-center justify-center`}>
            <Icon className={`w-6 h-6 text-${color}-600`} />
          </div>
          <div>
            <p className="text-2xl font-bold font-heading">{value}</p>
            <p className="text-sm text-slate-600">{label}</p>
            {subValue && <p className="text-xs text-slate-400 mt-1">{subValue}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  </motion.div>
);

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeClients: 0,
    internalTeam: 0,
    pendingApprovals: 0,
    activeTasks: 0,
    upcomingEvents: 0
  });
  const [recentUsers, setRecentUsers] = useState([]);
  const [systemHealth, setSystemHealth] = useState({
    backend: true,
    database: true,
    integrations: true
  });

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    // Run all fetches in parallel
    const [usersResult, approvalsResult, tasksResult, eventsResult, healthResult] = await Promise.allSettled([
      api.get('/admin/users'),
      api.get('/approvals/pending'),
      api.get('/tasks', { params: { status: 'in_progress' } }),
      api.get('/calendar/upcoming', { params: { days: 7 } }),
      api.get('/health'),
    ]);

    const rawUsers = usersResult.status === 'fulfilled' ? usersResult.value.data : null;
    const users = Array.isArray(rawUsers) ? rawUsers : (rawUsers?.items || []);
    const dbOk = usersResult.status === 'fulfilled';
    const backendOk = healthResult.status === 'fulfilled';

    const approvalsData = approvalsResult.status === 'fulfilled' ? approvalsResult.value.data : null;
    const pendingApprovals = approvalsData?.total ?? approvalsData?.items?.length ?? (Array.isArray(approvalsData) ? approvalsData.length : 0);

    const tasksData = tasksResult.status === 'fulfilled' ? tasksResult.value.data : [];
    const tasksList = Array.isArray(tasksData) ? tasksData : (tasksData?.items || []);
    const activeTasks = tasksList.length;

    const upcomingEvents = eventsResult.status === 'fulfilled' ? (eventsResult.value.data?.length || 0) : 0;

    const clients = users.filter(u => u.role === 'client');
    const internal = users.filter(u => u.role !== 'client');

    setStats({
      totalUsers: users.length,
      activeClients: clients.filter(u => u.is_active).length,
      internalTeam: internal.length,
      pendingApprovals,
      activeTasks,
      upcomingEvents,
    });

    setSystemHealth({
      backend: backendOk,
      database: dbOk,
      integrations: backendOk && dbOk,
    });

    const sortedUsers = [...users]
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .slice(0, 5);
    setRecentUsers(sortedUsers);
    setLoading(false);
  };

  const getRoleBadge = (role) => {
    const roles = {
      client: { label: 'Client', color: 'bg-blue-100 text-blue-700' },
      admin: { label: 'Admin', color: 'bg-red-100 text-red-700' },
      project_manager: { label: 'PM', color: 'bg-purple-100 text-purple-700' },
      content_account: { label: 'Content', color: 'bg-amber-100 text-amber-700' },
      creative_account: { label: 'Creative', color: 'bg-pink-100 text-pink-700' },
      execution_team: { label: 'Execution', color: 'bg-cyan-100 text-cyan-700' },
    };
    return roles[role] || roles.client;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold mb-1">Admin Dashboard</h1>
          <p className="text-slate-600 dark:text-slate-400">
            System overview and management
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => navigate('/dashboard/admin/users')}>
            <Users className="w-4 h-4 mr-2" />
            Manage Users
          </Button>
          <Button className="gds-btn-primary" onClick={() => navigate('/dashboard/admin/users')}>
            <UserPlus className="w-4 h-4 mr-2" />
            Add Team Member
          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <StatCard
          icon={Users}
          label="Total Users"
          value={stats.totalUsers}
          color="indigo"
          onClick={() => navigate('/dashboard/admin/users')}
        />
        <StatCard
          icon={Building2}
          label="Active Clients"
          value={stats.activeClients}
          color="blue"
          onClick={() => navigate('/dashboard/execution')}
        />
        <StatCard
          icon={Shield}
          label="Internal Team"
          value={stats.internalTeam}
          color="violet"
          onClick={() => navigate('/dashboard/admin/users')}
        />
        <StatCard
          icon={CheckSquare}
          label="Pending Approvals"
          value={stats.pendingApprovals}
          color="amber"
          onClick={() => navigate('/dashboard/approvals')}
        />
        <StatCard
          icon={ListTodo}
          label="Active Tasks"
          value={stats.activeTasks}
          color="cyan"
          onClick={() => navigate('/dashboard/tasks')}
        />
        <StatCard
          icon={Calendar}
          label="Events (7 days)"
          value={stats.upcomingEvents}
          color="emerald"
          onClick={() => navigate('/dashboard/calendar')}
        />
      </div>

      {/* Main Content */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Recent Users */}
        <Card className="lg:col-span-2 gds-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="font-heading flex items-center gap-2">
                <Users className="w-5 h-5 text-indigo-500" />
                Recent Users
              </CardTitle>
              <CardDescription>Latest registered accounts</CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard/admin/users')}>
              View All
              <ArrowUpRight className="w-4 h-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            {recentUsers.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Users className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>No users yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentUsers.map((user) => (
                  <div 
                    key={user.user_id}
                    className="flex items-center justify-between p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                        <span className="font-semibold text-indigo-600">
                          {user.name?.charAt(0)?.toUpperCase() || 'U'}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium">{user.name}</p>
                        <p className="text-sm text-slate-500">{user.email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={getRoleBadge(user.role).color}>
                        {getRoleBadge(user.role).label}
                      </Badge>
                      <Badge variant={user.is_active ? 'default' : 'secondary'}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* System Status & Quick Actions */}
        <div className="space-y-6">
          {/* System Health */}
          <Card className="gds-card">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Activity className="w-5 h-5 text-emerald-500" />
                System Health
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {[
                { label: 'Backend API', ok: systemHealth.backend },
                { label: 'Database', ok: systemHealth.database },
                { label: 'AI Services', ok: systemHealth.integrations },
              ].map(({ label, ok }) => (
                <div key={label} className="flex items-center justify-between">
                  <span className="text-sm flex items-center gap-2">
                    {ok
                      ? <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                      : <XCircle className="w-4 h-4 text-red-400" />}
                    {label}
                  </span>
                  <Badge className={ok ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                    {ok ? 'Operational' : 'Degraded'}
                  </Badge>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className="gds-card">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Settings className="w-5 h-5 text-slate-500" />
                Quick Actions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={() => navigate('/dashboard/admin/users')}
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Add Team Member
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={() => navigate('/dashboard/execution')}
              >
                <Building2 className="w-4 h-4 mr-2" />
                View All Clients
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={() => navigate('/dashboard/calendar')}
              >
                <Calendar className="w-4 h-4 mr-2" />
                Calendar
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => navigate('/dashboard/approvals')}
              >
                <CheckSquare className="w-4 h-4 mr-2" />
                Approvals
                {stats.pendingApprovals > 0 && (
                  <Badge className="ml-auto bg-amber-100 text-amber-700">{stats.pendingApprovals}</Badge>
                )}
              </Button>
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => navigate('/dashboard/admin/llm-usage')}
              >
                <BrainCircuit className="w-4 h-4 mr-2" />
                AI Usage &amp; Cost
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
